import { action, makeAutoObservable, observable } from 'mobx';
import { ACCTimerStatus } from '@domain/constant/accTimerType';

export interface ACCTimerOption {
  maxTimeSec: number;
  endCallback?: () => void;
  timerDelay?: number;
  debugWithName?: string;
}

// ACC Project Timer
export default class AccTimer {
  private _uuid = '';
  private _maxTimeSec = 1;
  private _endCallback: (() => void) | undefined = undefined;
  private _timerDelay = 1000; //ms

  private _tempStartTime = 0;
  private _remainingTime = 0;
  private _resumeTimer: ReturnType<typeof setTimeout> | null = null;

  private _debugWithName = ''; //for logging

  @observable private _timerStatus: ACCTimerStatus = ACCTimerStatus.UNINITIALIZED;
  @observable private _timeSec = 0;

  constructor(option?: ACCTimerOption) {
    if (option) this.initialize(option);
    this._uuid = accIntervalManager.generateUUID().toString();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  @action
  initialize(option: ACCTimerOption) {
    if (this._timerStatus === ACCTimerStatus.ACTIVE) return;

    if (this._uuid.length === 0 || accIntervalManager.has(this._uuid))
      this._uuid = accIntervalManager.generateUUID().toString();

    this._maxTimeSec = option.maxTimeSec;

    if (option.endCallback) this._endCallback = option.endCallback;

    if (option.timerDelay) this._timerDelay = option.timerDelay;

    if (option.debugWithName) this._debugWithName = option.debugWithName;

    this._timerStatus = ACCTimerStatus.INITIALIZED;
  }

  private intervalCallback = () => {
    if (this._timerStatus !== ACCTimerStatus.ACTIVE) return;

    this._timeSec = this._timeSec - this._timerDelay / 1000;

    this._tempStartTime = new Date().getTime();

    if (this._timeSec <= 0) {
      this.stopWithCallback();
    }
  };

  private clearResumeTimerIfExist() {
    if (this._resumeTimer !== null) {
      clearTimeout(this._resumeTimer);
      this._resumeTimer = null;
    }
  }

  @action
  start() {
    if (this._timerStatus === ACCTimerStatus.ACTIVE || this._timerStatus === ACCTimerStatus.UNINITIALIZED) return;

    this.clearResumeTimerIfExist();

    if (accIntervalManager.has(this._uuid)) {
      accIntervalManager.remove(this._uuid);
    }

    this._timeSec = this._maxTimeSec;
    this._timerStatus = ACCTimerStatus.ACTIVE;
    this._tempStartTime = new Date().getTime();

    accIntervalManager.add(this._uuid, setInterval(this.intervalCallback, this._timerDelay));
  }

  pause() {
    if (this._timerStatus === ACCTimerStatus.ACTIVE) {
      this.clearResumeTimerIfExist();

      this._timerStatus = ACCTimerStatus.INACTIVE;

      this._remainingTime = this._timerDelay - (new Date().getTime() - this._tempStartTime);
      accIntervalManager.remove(this._uuid);
    }
  }

  resume() {
    if (this._timerStatus === ACCTimerStatus.INACTIVE) {
      this._timerStatus = ACCTimerStatus.ACTIVE;

      this._resumeTimer = setTimeout(() => {
        this.intervalCallback();

        accIntervalManager.add(this._uuid, setInterval(this.intervalCallback, this._timerDelay));
        this._resumeTimer = null;
      }, this._remainingTime);

      this._remainingTime = 0;
    }
  }

  @action
  stop() {
    accIntervalManager.remove(this._uuid);

    this.clearResumeTimerIfExist();

    this._timerStatus = ACCTimerStatus.INITIALIZED;
  }

  stopWithCallback() {
    this.stop();

    if (this._endCallback) this._endCallback();
  }

  @action
  destroy() {
    this.stop();
    this._timerStatus = ACCTimerStatus.UNINITIALIZED;

    this._remainingTime = 0;
    this._tempStartTime = 0;

    this._maxTimeSec = 1;
    this._timerDelay = 1000; //ms
    this._endCallback = undefined;
    this._timeSec = 0;
    this._uuid = '';
  }

  // timeSec basically decreasing
  // 1 2 3 4 5 ...
  get increasingTime(): number {
    return this._maxTimeSec - this._timeSec;
  }

  // ... 5 4 3 2 1
  get decreasingTime(): number {
    return this._timeSec;
  }

  get status(): ACCTimerStatus {
    return this._timerStatus;
  }

  get isActive(): boolean {
    return this._timerStatus === ACCTimerStatus.ACTIVE;
  }
}

//interval manager
// TODO : test adding and removing interval
class ACCIntervalManager {
  private _intervalMap: Map<string, ReturnType<typeof setInterval>> = new Map<string, ReturnType<typeof setInterval>>();
  private _uuidIndex = 0;

  generateUUID() {
    return this._uuidIndex++;
  }

  has(name: string): boolean {
    return this._intervalMap.has(name);
  }

  add(name: string, interval: any) {
    if (this._intervalMap.get(name)) {
      throw new Error(`TimerManager::add interval \'${name}\' already exists!`);
    }

    this._intervalMap.set(name, interval);
  }

  remove(name: string) {
    const tempTimer = this._intervalMap.get(name);

    if (tempTimer) {
      clearInterval(tempTimer);
      this._intervalMap.delete(name);
    }
  }

  removeAll() {
    for (const name of Array.from(this._intervalMap.keys())) {
      this.remove(name);
    }
  }
}

const accIntervalManager = new ACCIntervalManager();

export { accIntervalManager };
